import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../../components/ui/Button';
import { Checkbox } from 'primereact/checkbox';
import { SelectButton } from 'primereact/selectbutton';
import { Skeleton } from 'primereact/skeleton';

import {
  useGetSources,
  useRouteSource,
  useRestoreCampaign,
  useSwitchPlayerCampaign,
  useRoutingState,
} from '../../../queries/hardware';
import { useEntitlements } from '../../../hooks/useEntitlements';
import { useCustomToast } from '../../Contexts/ToastContext';
import { buildEncoderColorMap, unwrapRoutingState } from '../sourceColors';

/**
 * Sources panel. Each card represents one Player→Encoder pair and renders
 * one of three states based on the backend's `is_on_hdmi` flag:
 *
 *  STATE 1 (regular campaign):
 *    - green border, mobile icon
 *    - "Route to Screens" + "Change Campaign" actions
 *  STATE 2 (HDMI-IN with a previous campaign stash):
 *    - indigo border, satellite icon, "HDMI Input Active" label
 *    - "Back to <prior>" (emerald, prominent) + Route + Change actions
 *  STATE 3 (HDMI-IN with no stash, e.g. fresh deploy or restore already used):
 *    - indigo border, no restore button — only Route + Change
 *
 * Server backs the state in HardwareService::listSources (which also returns
 * `available_campaigns` for the Change-Campaign picker).
 */
export default function SourcesPanel({ screens }) {
  const { t } = useTranslation();
  const { has } = useEntitlements();
  const { showToast } = useCustomToast();

  const { data, isLoading } = useGetSources({ enabled: has('hardware_control') });

  // listSources now returns `{ sources: [...], available_campaigns: [...] }`.
  // Older response shape was a flat array; tolerate both so a stale frontend
  // build doesn't crash mid-deploy.
  const payload = data?.data ?? data ?? {};
  const sources = Array.isArray(payload?.sources)
    ? payload.sources
    : Array.isArray(payload) ? payload : [];
  const availableCampaigns = Array.isArray(payload?.available_campaigns)
    ? payload.available_campaigns
    : [];

  // Live routing → stable colour per source (encoder). Matches ScreenGrid.
  const { data: routingData } = useRoutingState({ enabled: has('hardware_control') });
  const colorMap = buildEncoderColorMap(unwrapRoutingState(routingData).encoders);

  const routeMut    = useRouteSource();
  const restoreMut  = useRestoreCampaign();
  const switchMut   = useSwitchPlayerCampaign();

  // ── Route-to-Screens picker (existing flow) ─────────────────────────────
  const [routePicker, setRoutePicker]         = useState(null);  // source row or null
  const [selectedScreens, setSelectedScreens] = useState([]);
  // What to route: 'va' (video+audio, default) | 'v' (video only) | 'a' (audio only).
  const [routeOptions, setRouteOptions]       = useState('va');

  const optionItems = [
    { label: t('remote_control.sources.opt_video_audio'), value: 'va' },
    { label: t('remote_control.sources.opt_video_only'),  value: 'v' },
    { label: t('remote_control.sources.opt_audio_only'),  value: 'a' },
  ];

  useEffect(() => {
    if (!routePicker) return;
    setSelectedScreens((screens ?? []).map((s) => s.id));
    setRouteOptions('va');   // reset to the safe default each time the dialog opens
  }, [routePicker, screens]);

  const toggleAll = (checked) =>
    setSelectedScreens(checked ? (screens ?? []).map((s) => s.id) : []);
  const toggleOne = (id, checked) =>
    setSelectedScreens((prev) =>
      checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)
    );
  const allSelected = (screens ?? []).length > 0 && selectedScreens.length === (screens ?? []).length;

  const doRoute = () => {
    if (!routePicker) return;
    if (selectedScreens.length === 0) {
      showToast('error', t('remote_control.title'), t('remote_control.channels.no_screens_selected'));
      return;
    }
    routeMut.mutate(
      { encoder_id: routePicker.encoder_id, screen_ids: selectedScreens, options: routeOptions },
      {
        onSuccess: () => {
          const names = (screens ?? [])
            .filter((s) => selectedScreens.includes(s.id))
            .map((s) => s.name)
            .join(', ');
          showToast('success', t('remote_control.title'),
            t('remote_control.sources.routed_success', {
              player: routePicker.player_name || t('remote_control.sources.no_player'),
              screens: names,
            }));
          setRoutePicker(null);
        },
      }
    );
  };

  // ── Change-Campaign picker ──────────────────────────────────────────────
  // Tracks which source card opened it AND which campaign is selected, so
  // we can submit with both pieces. Dialog closes on success.
  const [changePicker, setChangePicker] = useState(null);   // source row or null
  const [pickedCampaignId, setPickedCampaignId] = useState(null);

  useEffect(() => {
    setPickedCampaignId(changePicker?.campaign_id ?? null);
  }, [changePicker]);

  const doSwitchCampaign = () => {
    if (!changePicker || !pickedCampaignId) return;
    switchMut.mutate(
      { device_id: changePicker.player_id, campaign_id: pickedCampaignId },
      {
        onSuccess: (res) => {
          const campaignName = res?.data?.campaign_name
            ?? availableCampaigns.find((c) => c.id === pickedCampaignId)?.name
            ?? '';
          showToast('success', t('remote_control.title'),
            t('remote_control.sources.switched', {
              player: changePicker.player_name || t('remote_control.sources.no_player'),
              campaign: campaignName,
            }));
          setChangePicker(null);
        },
      }
    );
  };

  // ── Restore (one-shot, no dialog) ───────────────────────────────────────
  const doRestore = (source) => {
    restoreMut.mutate(
      { device_id: source.player_id },
      {
        onSuccess: (res) => {
          const campaignName = res?.data?.restored_campaign_name
            ?? source.previous_campaign_name
            ?? '';
          showToast('success', t('remote_control.title'),
            t('remote_control.sources.restored', {
              player: source.player_name || t('remote_control.sources.no_player'),
              campaign: campaignName,
            }));
        },
      }
    );
  };

  return (
    <>
      <div className="remote-receiver-name mt-3">
        <i className="pi pi-th-large me-1" /> {t('remote_control.sources.title')}
      </div>

      {isLoading ? (
        <Skeleton height="4rem" className="mb-2" />
      ) : sources.length === 0 ? (
        <p className="small text-muted mb-2">{t('remote_control.sources.empty')}</p>
      ) : (
        sources.map((s) => {
          const onHdmi = !!s.is_on_hdmi;
          const hasPrev = onHdmi && !!s.previous_campaign_id;
          const isRestoring = restoreMut.isLoading && restoreMut.variables?.device_id === s.player_id;
          // This source's colour (matches the screen cards it's routed to).
          const sourceColor = s.encoder_id ? colorMap[s.encoder_id] : null;

          return (
            <div
              className={`remote-source-card${onHdmi ? ' is-hdmi' : ''}`}
              key={s.encoder_id ?? s.player_id}
              style={sourceColor
                ? { borderInlineStartWidth: 6, borderInlineStartColor: sourceColor }
                : undefined}
            >
              <div className="d-flex align-items-center gap-2">
                <span className="remote-source-icon">
                  <i className={onHdmi ? 'pi pi-wifi' : 'pi pi-mobile'} />
                </span>
                <div className="d-flex flex-column flex-grow-1 min-w-0">
                  <span className="remote-source-name text-truncate d-flex align-items-center gap-1">
                    {sourceColor && (
                      <span
                        aria-hidden="true"
                        style={{
                          display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                          background: sourceColor, flex: '0 0 auto',
                        }}
                      />
                    )}
                    {s.player_name || t('remote_control.sources.no_player')}
                  </span>
                  <span className="remote-source-meta">
                    {onHdmi ? (
                      <><i className="pi pi-circle-fill me-1" style={{ fontSize: '0.5rem', color: 'var(--pp-primary)' }} />
                        {t('remote_control.sources.hdmi_active')}</>
                    ) : (
                      <>{t('remote_control.sources.campaign')}:{' '}
                        {s.campaign_name || t('remote_control.sources.live')}</>
                    )}
                  </span>
                </div>
              </div>

              <div className="remote-source-meta">
                <strong>{t('remote_control.sources.encoder')}:</strong>{' '}
                {s.encoder_name
                  ? (s.encoder_port ? `${s.encoder_name} (port ${s.encoder_port})` : s.encoder_name)
                  : t('remote_control.sources.no_encoder')}
              </div>

              {hasPrev && (
                <button
                  className="remote-source-restore"
                  disabled={isRestoring}
                  onClick={() => doRestore(s)}
                >
                  <i className={isRestoring ? 'pi pi-spin pi-spinner' : 'pi pi-reply'} />
                  {t('remote_control.sources.back_to', { campaign: s.previous_campaign_name })}
                </button>
              )}

              <button
                className="remote-source-cta"
                disabled={!s.encoder_id}
                onClick={() => setRoutePicker(s)}
              >
                <i className="pi pi-arrow-right" />
                {t('remote_control.sources.route_to_screens')}
              </button>

              <button
                className="remote-source-change"
                disabled={!s.player_id || availableCampaigns.length === 0}
                onClick={() => setChangePicker(s)}
              >
                <i className="pi pi-sync" />
                {t('remote_control.sources.change_campaign')}
                <i className="pi pi-chevron-down" style={{ fontSize: '0.65rem' }} />
              </button>
            </div>
          );
        })
      )}

      {/* Route-to-Screens dialog */}
      <Dialog
        header={routePicker
          ? t('remote_control.sources.route_to_screens') + ' — ' + (routePicker.player_name || '')
          : ''}
        visible={!!routePicker}
        onHide={() => setRoutePicker(null)}
        style={{ width: 'min(95vw, 420px)' }}
        modal
      >
        <p className="small text-muted mb-3">{t('remote_control.sources.route_prompt')}</p>

        <div className="d-flex flex-column gap-2 mb-3">
          <label className="d-flex align-items-center gap-2 fw-semibold">
            <Checkbox checked={allSelected} onChange={(e) => toggleAll(e.checked)} />
            {t('remote_control.channels.all_screens')}
          </label>
          <div style={{ borderTop: '1px solid #e5e7eb' }} />
          {(screens ?? []).map((s) => (
            <label key={s.id} className="d-flex align-items-center gap-2">
              <Checkbox
                checked={selectedScreens.includes(s.id)}
                onChange={(e) => toggleOne(s.id, e.checked)}
              />
              <span>{s.name}</span>
              {s.location && <span className="text-muted small">— {s.location}</span>}
            </label>
          ))}
        </div>

        {/* What to route: video+audio (default) / video only / audio only.
            Audio-only lets the venue send sound from a different encoder than
            the picture. */}
        <div className="mb-3">
          <label className="small fw-semibold d-block mb-1">
            {t('remote_control.sources.route_what')}
          </label>
          <SelectButton
            value={routeOptions}
            onChange={(e) => e.value && setRouteOptions(e.value)}
            options={optionItems}
            allowEmpty={false}
          />
        </div>

        <div className="d-flex justify-content-end gap-2">
          <Button label={t('buttons.cancel')} severity="secondary" outlined onClick={() => setRoutePicker(null)} />
          <Button
            label={t('remote_control.sources.route_to_screens')}
            loading={routeMut.isLoading}
            onClick={doRoute}
            style={{ background: 'linear-gradient(135deg, #10b981, #059669)', border: 'none' }}
          />
        </div>
      </Dialog>

      {/* Change-Campaign dialog */}
      <Dialog
        header={changePicker
          ? t('remote_control.sources.change_campaign') + ' — ' + (changePicker.player_name || '')
          : ''}
        visible={!!changePicker}
        onHide={() => setChangePicker(null)}
        style={{ width: 'min(95vw, 420px)' }}
        modal
      >
        <p className="small text-muted mb-3">{t('remote_control.sources.select_campaign')}</p>

        <div className="d-flex flex-column gap-1 mb-3" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
          {availableCampaigns.length === 0 ? (
            <p className="small text-muted">{t('remote_control.sources.empty')}</p>
          ) : (
            availableCampaigns.map((c) => (
              <label
                key={c.id}
                className="d-flex align-items-center gap-2 p-2 rounded"
                style={{
                  cursor: 'pointer',
                  // Theme-aware selected highlight (readable text in dark mode too).
                  background: pickedCampaignId === c.id ? 'color-mix(in srgb, var(--pp-btn-honey) 10%, transparent)' : 'transparent',
                  color: 'var(--pp-text-primary)',
                  border: pickedCampaignId === c.id ? '1px solid var(--pp-btn-honey)' : '1px solid transparent',
                }}
              >
                <input
                  type="radio"
                  name="picked-campaign"
                  checked={pickedCampaignId === c.id}
                  onChange={() => setPickedCampaignId(c.id)}
                />
                <span>{c.name}</span>
                {changePicker?.campaign_id === c.id && (
                  <span className="text-muted small ms-auto">({t('remote_control.channels.active')})</span>
                )}
              </label>
            ))
          )}
        </div>

        <div className="d-flex justify-content-end gap-2">
          <Button label={t('buttons.cancel')} severity="secondary" outlined onClick={() => setChangePicker(null)} />
          <Button
            label={t('remote_control.sources.change_campaign')}
            loading={switchMut.isLoading}
            disabled={!pickedCampaignId || pickedCampaignId === changePicker?.campaign_id}
            onClick={doSwitchCampaign}
          />
        </div>
      </Dialog>
    </>
  );
}
